import React, { useMemo, useState } from "react";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import { normListId } from "../../common/dualListBox";
import {
  DEFAULT_STUDY_COLOR,
  pickStudyColorRaw,
  resolveStudyDisplayColor,
  studyBadgeTextColor,
} from "../../../utils/studyColorUtils";
import "./StudyAssignmentPicker.scss";

const PICKER_HINT =
  "Use Available to pick studies (same style as table filters). Selected show below as a list — hover for full name.";

function StudyPickerInfoHint({ text }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`study-assignment-picker__info-hint${open ? " is-open" : ""}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="study-assignment-picker__info-hint-btn"
        aria-label={text}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <i className="fas fa-info-circle" aria-hidden="true" />
      </button>
      <div className="study-assignment-picker__info-hint-popover" role="tooltip">
        {text}
      </div>
    </div>
  );
}

function pickStudyId(study) {
  return study?.StudyId ?? study?.value ?? study?.StudyID ?? "";
}

function parseStudyLabelParts(label = "") {
  const text = String(label || "").trim();
  const dash = text.indexOf(" - ");
  if (dash > 0) {
    return {
      number: text.slice(0, dash).trim(),
      name: text.slice(dash + 3).trim(),
    };
  }
  return { number: text, name: "" };
}

function findStudyOption(options, studyId) {
  const id = normListId(studyId);
  return (options || []).find((opt) => normListId(opt?.value) === id) || null;
}

function resolveStudyMeta(row, options = []) {
  const option = findStudyOption(options, pickStudyId(row));
  const parsed = parseStudyLabelParts(row?.label || option?.label || "");
  const number =
    row?.studyNumber ||
    option?.studyNumber ||
    parsed.number ||
    "Study";
  const name = row?.studyName || option?.studyName || parsed.name || "";
  const label =
    row?.label ||
    option?.label ||
    (name ? `${number} - ${name}` : number);
  const rawColor =
    pickStudyColorRaw(row) ||
    pickStudyColorRaw(option) ||
    pickStudyColorRaw({ studyColor: row?.studyColor });
  const color = resolveStudyDisplayColor(rawColor, DEFAULT_STUDY_COLOR);

  return { number, name, label, color, rawColor };
}

function buildStudyRow(option, fallbackId) {
  if (!option && !fallbackId) return null;
  const meta = resolveStudyMeta(
    {
      StudyId: option?.value || fallbackId,
      label: option?.label,
      studyNumber: option?.studyNumber,
      studyName: option?.studyName,
      StudySchColor: option?.StudySchColor,
      StudyColor: option?.StudyColor,
      studyColor: option?.studyColor,
    },
    option ? [option] : [],
  );

  return {
    StudyId: option?.value || fallbackId,
    label: meta.label,
    studyNumber: meta.number,
    studyName: meta.name,
    studyColor: meta.rawColor,
    StudySchColor: pickStudyColorRaw(option) || meta.rawColor,
    StudyColor: option?.StudyColor || meta.rawColor,
  };
}

/** Map org study catalog options → assigned-study rows for the picker. */
export function mapStudyOptionsToAssignedRows(options = []) {
  return (options || [])
    .map((option) => buildStudyRow(option, option?.value))
    .filter(Boolean);
}

function StudyAssignmentPicker({
  options = [],
  selected = [],
  onChange,
  emptyMessage = "No studies available.",
  readOnly = false,
  readOnlyMessage = "System Administrator has access to all studies (read-only).",
}) {
  const [selectedSearch, setSelectedSearch] = useState("");

  const selectedRows = useMemo(() => {
    const seen = new Set();
    return (selected || [])
      .map((row) => {
        const option = findStudyOption(options, pickStudyId(row));
        const built = buildStudyRow(option, pickStudyId(row));
        if (!built) return null;
        const rawColor =
          pickStudyColorRaw(row) ||
          pickStudyColorRaw(option) ||
          pickStudyColorRaw(built) ||
          "";
        return {
          ...built,
          ...row,
          StudyId: pickStudyId(row) || built.StudyId,
          label: row?.label || built.label,
          studyNumber: row?.studyNumber || built.studyNumber,
          studyName: row?.studyName || built.studyName,
          studyColor: rawColor,
          StudySchColor:
            pickStudyColorRaw(row) ||
            pickStudyColorRaw(option) ||
            built.StudySchColor ||
            rawColor,
        };
      })
      .filter((row) => {
        if (!row) return false;
        const id = normListId(pickStudyId(row));
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
  }, [selected, options]);

  const selectedIds = useMemo(
    () => selectedRows.map((row) => pickStudyId(row)).filter(Boolean),
    [selectedRows],
  );

  const selectedIdSet = useMemo(
    () => new Set(selectedIds.map((id) => normListId(id))),
    [selectedIds],
  );

  const availableOptions = useMemo(
    () =>
      (options || []).filter(
        (option) => !selectedIdSet.has(normListId(option?.value)),
      ),
    [options, selectedIdSet],
  );

  const availableCount = availableOptions.length;
  const pickerPlaceholder = `Select studies (available ${availableCount})`;

  const filteredSelectedRows = useMemo(() => {
    const term = selectedSearch.trim().toLowerCase();
    if (!term) return selectedRows;
    return selectedRows.filter((row) => {
      const meta = resolveStudyMeta(row, options);
      return (
        meta.label.toLowerCase().includes(term) ||
        meta.number.toLowerCase().includes(term) ||
        meta.name.toLowerCase().includes(term)
      );
    });
  }, [selectedRows, selectedSearch, options]);

  const handleMultiChange = (ids) => {
    if (readOnly) return;
    const next = (ids || [])
      .map((id) => {
        const existing = selectedRows.find(
          (row) => normListId(pickStudyId(row)) === normListId(id),
        );
        if (existing) return existing;
        return buildStudyRow(findStudyOption(options, id), id);
      })
      .filter(Boolean);
    onChange?.(next);
  };

  const removeStudy = (row) => {
    if (readOnly) return;
    const id = normListId(pickStudyId(row));
    onChange?.(
      selectedRows.filter((item) => normListId(pickStudyId(item)) !== id),
    );
  };

  return (
    <div
      className={`study-assignment-picker${
        readOnly ? " study-assignment-picker--readonly" : ""
      }`}
    >
      {options.length === 0 ? (
        <p className="study-assignment-picker__empty study-assignment-picker__empty--inline">
          {emptyMessage}
        </p>
      ) : readOnly ? (
        <p className="study-assignment-picker__empty study-assignment-picker__empty--inline study-assignment-picker__readonly-banner">
          {readOnlyMessage} All {options.length} selected.
        </p>
      ) : availableCount === 0 ? (
        <p className="study-assignment-picker__empty study-assignment-picker__empty--inline">
          All studies selected ({options.length}).
        </p>
      ) : (
        <div
          className="study-assignment-picker__picker"
          style={{ "--picker-placeholder": `"${pickerPlaceholder}"` }}
        >
          <BadgeMultiSelect
            options={availableOptions}
            value={selectedIds}
            onChange={handleMultiChange}
            emptyLabel={pickerPlaceholder}
            addLabel="+"
            popoverTitle="Select studies"
            popoverSubtitle={`${availableCount} available`}
            showSearch
            inlinePopover
            renderOption={(option) => {
              const meta = resolveStudyMeta(
                {
                  StudyId: option?.value,
                  label: option?.label,
                  studyNumber: option?.studyNumber,
                  studyName: option?.studyName,
                  studyColor: option?.studyColor,
                  StudySchColor: option?.StudySchColor,
                  StudyColor: option?.StudyColor,
                },
                [option],
              );
              const displayName = meta.name || meta.label;
              return (
                <span className="study-assignment-picker__option">
                  <span
                    className="study-assignment-picker__study-code"
                    style={{
                      backgroundColor: meta.color,
                      color: studyBadgeTextColor(meta.color),
                    }}
                    title={meta.number}
                  >
                    {meta.number}
                  </span>
                  <span
                    className="study-assignment-picker__study-name"
                    title={displayName}
                  >
                    {displayName}
                  </span>
                </span>
              );
            }}
          />
        </div>
      )}

      <section className="study-assignment-picker__panel study-assignment-picker__panel--selected">
        <input
          type="search"
          className="study-assignment-picker__search"
          placeholder="Search studies..."
          value={selectedSearch}
          onChange={(event) => setSelectedSearch(event.target.value)}
          aria-label="Search selected studies"
        />

        <header className="study-assignment-picker__panel-head">
          <div className="study-assignment-picker__panel-head-main">
            <span className="study-assignment-picker__panel-label">
              {readOnly ? "All studies" : "Selected"}
            </span>
            {!readOnly ? <StudyPickerInfoHint text={PICKER_HINT} /> : null}
            <span className="study-assignment-picker__count">
              {selectedRows.length}
            </span>
          </div>
        </header>

        <div className="study-assignment-picker__list-wrap">
          {selectedRows.length === 0 ? (
            <p className="study-assignment-picker__empty">
              No studies selected yet.
            </p>
          ) : filteredSelectedRows.length === 0 ? (
            <p className="study-assignment-picker__empty">
              No selected studies match your search.
            </p>
          ) : (
            <ul className="study-assignment-picker__list">
              {filteredSelectedRows.map((row) => {
                const meta = resolveStudyMeta(row, options);
                const displayName = meta.name || meta.label;
                return (
                  <li
                    key={normListId(pickStudyId(row)) || String(pickStudyId(row))}
                    className="study-assignment-picker__list-item"
                    title={meta.label}
                  >
                    <span
                      className="study-assignment-picker__study-code"
                      style={{
                        backgroundColor: meta.color,
                        color: studyBadgeTextColor(meta.color),
                      }}
                      title={meta.number}
                    >
                      {meta.number}
                    </span>
                    <span
                      className="study-assignment-picker__study-name"
                      title={displayName}
                    >
                      {displayName}
                    </span>
                    {!readOnly ? (
                      <button
                        type="button"
                        className="study-assignment-picker__list-remove"
                        aria-label={`Remove ${meta.label}`}
                        onClick={() => removeStudy(row)}
                      >
                        ×
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

export default StudyAssignmentPicker;
